// z-base-32 codec used for canonical PeerId strings and DNS labels.
//
// Alphabet :
//
//   ybndrfg8ejkmcpqxot1uwisza345h769
//
// Omits visually confusable characters (0/O, 1/l/I), fully lowercase,
// no padding. Every 5 input bits map to one output character; 32-byte
// inputs produce 52 characters (256 bits → 260 output bits; trailing 4
// bits are padding and MUST decode to zero).
//
// Ported byte-for-byte from crates/identity/src/zbase32.rs.

export const Z32_ALPHABET = "ybndrfg8ejkmcpqxot1uwisza345h769";

/** Reverse lookup table; `DECODE[b] === 0xff` means "not in alphabet". */
const DECODE: Uint8Array = (() => {
  const t = new Uint8Array(256);
  t.fill(0xff);
  for (let i = 0; i < 32; i++) {
    t[Z32_ALPHABET.charCodeAt(i)] = i;
  }
  return t;
})();

export class Z32Error extends Error {
  override name = "Z32Error";
  constructor(message: string) {
    super(message);
  }
}

/** z-base-32 string has wrong length for the expected byte count. */
export class Z32WrongLengthError extends Z32Error {
  override name = "Z32WrongLengthError";
  constructor(
    public readonly got: number,
    public readonly expected: number,
  ) {
    super(`z-base-32 wrong length: got ${got} chars, expected ${expected}`);
  }
}

/** Character outside the z-base-32 alphabet. */
export class Z32InvalidCharError extends Z32Error {
  override name = "Z32InvalidCharError";
  constructor(public readonly charCode: number) {
    super(
      `invalid z-base-32 character 0x${charCode.toString(16).padStart(2, "0")}`,
    );
  }
}

/** Trailing padding bits were non-zero. */
export class Z32NonZeroPaddingError extends Z32Error {
  override name = "Z32NonZeroPaddingError";
  constructor() {
    super("z-base-32 non-zero padding bits");
  }
}

function divCeil(a: number, b: number): number {
  return Math.floor((a + b - 1) / b);
}

/** Encode bytes to z-base-32. Output length is `ceil(input.length * 8 / 5)`. */
export function encode(input: Uint8Array): string {
  if (input.length === 0) return "";
  const outLen = divCeil(input.length * 8, 5);
  const out: string[] = new Array(outLen);
  let idxOut = 0;

  let buf = 0;
  let bits = 0;
  for (const b of input) {
    // Keep `buf` within safe integer range: bits never exceeds 12 below
    // the next flush, so `buf` stays well under 2^32.
    buf = ((buf << 8) | b) >>> 0;
    bits += 8;
    while (bits >= 5) {
      bits -= 5;
      const idx = (buf >>> bits) & 0x1f;
      out[idxOut++] = Z32_ALPHABET[idx]!;
    }
  }
  if (bits > 0) {
    const idx = (buf << (5 - bits)) & 0x1f;
    out[idxOut++] = Z32_ALPHABET[idx]!;
  }
  return out.join("");
}

/**
 * Decode a z-base-32 string. `expectedBytes` tells the decoder the exact
 * output length; any trailing padding bits MUST be zero.
 */
export function decode(s: string, expectedBytes: number): Uint8Array {
  const expectedChars = divCeil(expectedBytes * 8, 5);
  if (s.length !== expectedChars) {
    throw new Z32WrongLengthError(s.length, expectedChars);
  }

  const out = new Uint8Array(expectedBytes);
  let outIdx = 0;
  let buf = 0;
  let bits = 0;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    const v = DECODE[c] ?? 0xff;
    if (v === 0xff) {
      throw new Z32InvalidCharError(c);
    }
    buf = ((buf << 5) | v) >>> 0;
    bits += 5;
    if (bits >= 8) {
      bits -= 8;
      out[outIdx++] = (buf >>> bits) & 0xff;
    }
  }

  if (outIdx !== expectedBytes) {
    throw new Z32WrongLengthError(outIdx, expectedBytes);
  }

  if (bits > 0) {
    const paddingMask = (1 << bits) - 1;
    if ((buf & paddingMask) !== 0) {
      throw new Z32NonZeroPaddingError();
    }
  }

  return out;
}
